import { dom } from '@omegagrid/core';
import { FormContainer } from '@omegagrid/form';
import rp, {
	AppComponent, CodeEditor, GridEditor, ParsedComponentId, SqlCompileView, SqlEditor,
	SqlEditorContainer, Application,
	SqlEditorEvent, SqlEditorQueryPlan, SqlEditorResult, SqlQueryPlan,
	SyncGridEditorAdapter
} from '@reporting/rp-app-host';
import {
	ExtSyncEditorAdapter,
	FileEditorAdapter, FileFormAdapter, FileGridEditorAdapter, ParamGridEditorAdapter, ParamSqlEditorAdapter, ProjectCopyFormAdapter, ProjectGridEditorAdapter,
	QueryFormAdapter, QueryGridEditorAdapter, QuerySqlEditorAdapter, QuerySqlParameterListAdapter, ParamFormAdapter,
	SqlDependencyGridEditorAdapter,
	ExtensionHtmlEditorAdapter,
	ExtensionSheetEditorAdapter
} from './adapters';
import { ExtensionInternalHost, ExtensionSandboxHost, SheetEditor, Sidebar } from './components';
import { ProjectFormAdapter } from './adapters/projectFormAdapter';
import { ExtensionApplication, HtmlEditor } from '.';
import { Query, Parameter, Resource } from '@reporting/rp-types/ext';
import { ExternalResourcesGridEditorAdapter } from './adapters/externalsDataGridAdapter';
import { requiresSandbox } from './utils';
import { ExtensionHost } from './components/extensionHost';

const createMainSidebar = () => dom.createElement<Sidebar>('rp-mf-ext-sidebar');

// only a sidebar that exists is refreshed: `get` would create one outside the module just to refresh it
const tryRefreshSidebar = (component?: string) => {
	if (rp.host.components.has('extensions')) (rp.host.components.get('extensions') as Sidebar).refresh(component);
};

const createProjectForm = (app: ExtensionApplication, componentId: ParsedComponentId) =>
	dom.createElement<FormContainer>('og-form-container', {
		adapter: new ProjectFormAdapter(componentId.ids[1] ? parseInt(componentId.ids[1]) : null),
		listeners: [['form.save', () => tryRefreshSidebar('projects')]]
	});

/** A copy may land in this company too: the project grid (when open) and the project list of the sidebar show it */
const tryRefreshProjects = () => {
	if (rp.host.components.has('extensions-projectGrid')) (rp.host.components.get('extensions-projectGrid') as GridEditor).refresh();
	tryRefreshSidebar('projects');
};

const createProjectCopyForm = (app: ExtensionApplication, componentId: ParsedComponentId) =>
	dom.createElement<FormContainer>('og-form-container', {
		adapter: new ProjectCopyFormAdapter(parseInt(componentId.ids[1])),
		listeners: [['form.save', tryRefreshProjects]]
	});

const createProjectGrid = (app: ExtensionApplication, componentId: ParsedComponentId) =>
	dom.createElement<GridEditor>('rp-editor-grid', {
		adapter: new ProjectGridEditorAdapter(),
		listeners: [['save', () => tryRefreshSidebar('projects')]]
	});

const createExternalResourceGrid = (app: ExtensionApplication, componentId: ParsedComponentId) =>
	dom.createElement<GridEditor>('rp-editor-grid', {
		adapter: new ExternalResourcesGridEditorAdapter(parseInt(componentId.ids[1]))
	});


const createSqlDependencyGrid = (app: ExtensionApplication, componentId: ParsedComponentId) =>
	dom.createElement<GridEditor>('rp-editor-grid', {
		adapter: new SqlDependencyGridEditorAdapter(parseInt(componentId.ids[1]))
	});

const createFileForm = (app: ExtensionApplication, componentId: ParsedComponentId) => {
	const component = dom.createElement<FormContainer>('og-form-container', {
		listeners: [['form.save', () => tryRefreshSidebar('files')]]
	});
	const adapter = new FileFormAdapter(app.activeProject?.id_custom_page, componentId.ids[1] ? parseInt(componentId.ids[1]) : null);
	adapter.duplicate = componentId.ids[2] === 'copy';
	component.adapter = adapter;
	return component as unknown as AppComponent;
};

const createFileEditor = (app: ExtensionApplication, componentId: ParsedComponentId) =>
	dom.createElement<CodeEditor>('rp-editor-code', {
		adapter: new FileEditorAdapter(componentId.ids[1] ? parseInt(componentId.ids[1]) : null)
	});

const createHtmlEditor = async (app: ExtensionApplication, componentId: ParsedComponentId) => {
	const resource = await rp.ms('extensions').get<Resource>(`resources/${componentId.ids[1]}`);
	return dom.createElement<HtmlEditor>('rp-editor-html', {
		adapter: new ExtensionHtmlEditorAdapter(componentId.ids[1] ? parseInt(componentId.ids[1]) : null),
		container: app.container
	});
}

const createSheetEditor = (app: ExtensionApplication, componentId: ParsedComponentId) =>
	dom.createElement<SheetEditor>('rp-editor-sheet', {
		adapter: new ExtensionSheetEditorAdapter(componentId.ids[1] ? parseInt(componentId.ids[1]) : null)
	});

const createFileGrid = (app: ExtensionApplication, componentId: ParsedComponentId) =>
	dom.createElement<GridEditor>('rp-editor-grid', {
		adapter: new FileGridEditorAdapter(componentId.ids[1] ? parseInt(componentId.ids[1]) : null),
		listeners: [['save', () => tryRefreshSidebar('files')]]
	});

const createQueryForm = (app: ExtensionApplication, componentId: ParsedComponentId) => {
	const component = dom.createElement<FormContainer>('og-form-container', {
		listeners: [['form.save', () => tryRefreshSidebar('queries')]]
	});
	const adapter = new QueryFormAdapter(app.activeProject?.id_custom_page, componentId.ids[1] ? parseInt(componentId.ids[1]) : null);
	adapter.duplicate = componentId.ids[2] === 'copy';
	component.adapter = adapter;
	return component as unknown as AppComponent;
};

const createQueryGrid = (app: ExtensionApplication, componentId: ParsedComponentId) =>
	dom.createElement<GridEditor>('rp-editor-grid', {
		adapter: new QueryGridEditorAdapter(componentId.ids[1] ? parseInt(componentId.ids[1]) : null),
		listeners: [['save', () => tryRefreshSidebar('queries')]]
	});

const createParameterGrid = (app: ExtensionApplication, componentId: ParsedComponentId) =>
	dom.createElement<GridEditor>('rp-editor-grid', {
		adapter: new ParamGridEditorAdapter(parseInt(componentId.ids[1]))
	});


const createSqlEditorBeforeExecHandler = (component: AppComponent, app: Application, componentId: ParsedComponentId) => {
	return () => {
		const resultId = `${componentId.type}-queryResult-${componentId.ids[1]}`;
		if (app.host.components.has(resultId)) {
			(app.host.components.components.get(resultId) as SqlEditorResult).clear();
		}
	}
}

const createSqlEditorResultHandler = (component: AppComponent, app: Application, componentId: ParsedComponentId) => {
	// datasets and messages in stream order - a warning belongs in front of the dataset it preceded
	const results: SqlEditorEvent[] = [];
	const plans: SqlQueryPlan[] = [];
	const tabColor = rp.settings.get('result.tab.color') === '0' ? null : rp.settings.get('result.tab.color');

	return async (e: SqlEditorEvent) => {
		if (e.dataset || e.message) results.push(e);
		if (e.queryPlan) plans.push(e.queryPlan);

		if (e.dataset || e.message) {
			const resultId = `${componentId.type}-queryResult-${componentId.ids[1]}`;
			const editorResult = (await app.host.open(resultId, {
				title: `${component.name ?? ''} [result]`.trimStart(),
				color: tabColor,
				persistent: false
			})) as SqlEditorResult;
			let result: SqlEditorEvent;
			while (result = results.shift()) {
				if (result.dataset) {
					if (result.dataset.index == 0) editorResult.clear();
					editorResult.addDataset(result.dataset);
				} else {
					editorResult.addMessage(result.message);
				}
			}
		}

		if (e.queryPlan) {
			const resultId = `${componentId.type}-queryPlan-${componentId.ids[1]}`;
			const editorQueryPlan = (await app.host.open(resultId, {
				title: `${component.name} [plan]`,
				color: tabColor,
				persistent: false
			})) as SqlEditorQueryPlan;
			let plan: SqlQueryPlan;
			while (plan = plans.shift()) {
				if (plan.index == 0) editorQueryPlan.clear();
				editorQueryPlan.addPlan(plan);
			}
		}
	}
}

const createSqlCompileView = (component: SqlEditorContainer, e: SqlEditorEvent) => {
	const view = dom.createElement<SqlCompileView>('rp-editor-sql-compile', {
			sql: e.sql,
			offset: e.offset,
			tail: e.tail
	});
	const tabColor = rp.settings.get('result.tab.color') === '0' ? null : rp.settings.get('result.tab.color');
	rp.host.open(null, {
		component: view,
		title: `${component.name ?? ''} [compile]`.trimStart(),
		color: tabColor,
		persistent: false
	});
}

const createSqlEditor = (app: Application, componentId: ParsedComponentId) => {
	const query: Query = {id_thread: 0, description: 'New query'};
	const component = dom.createElement<SqlEditor>('rp-editor-sql');
	component.adapter = new QuerySqlEditorAdapter(query);
	component.addEventListener('sqleditor.beforeExec', createSqlEditorBeforeExecHandler(component, app, componentId));
	component.addEventListener('sqleditor.result',  createSqlEditorResultHandler(component, app, componentId));
	return component;
}

const createQuerySqlEditor = (app: Application, componentId: ParsedComponentId, query?: Query) => {
	query = query || {id_thread: 0, description: 'New query'};
	const component = dom.createElement<SqlEditorContainer>('rp-editor-sql-container');
	component.editorAdapter = new QuerySqlEditorAdapter(
		query,
		() => component.parameterList.getParameterValues()
	);
	component.parameterListAdapter = new QuerySqlParameterListAdapter(query);
	component.addEventListener('sqleditor.beforeExec', createSqlEditorBeforeExecHandler(component, app, componentId));
	component.addEventListener('sqleditor.result', createSqlEditorResultHandler(component, app, componentId));
	component.addEventListener('sqleditor.compile', (e: SqlEditorEvent) => createSqlCompileView(component, e));
	return component;
}

const createParameterSqlEditor = async (app: Application, componentId: ParsedComponentId, type: 'content'|'value') => {
	const component = dom.createElement<SqlEditor>('rp-editor-sql');
	const parameter = await rp.ms('extensions').get<Parameter>(`parameters/${componentId.ids[1]}`);
	component.adapter = new ParamSqlEditorAdapter(parameter, type);
	component.addEventListener('sqleditor.result',  createSqlEditorResultHandler(component, app, componentId));
	return component;
}

const createParameterContentSqlEditor = async (app: Application, componentId: ParsedComponentId) =>
	createParameterSqlEditor(app, componentId, 'content');

const createParameterValueSqlEditor = async (app: Application, componentId: ParsedComponentId) =>
	createParameterSqlEditor(app, componentId, 'value');

const createQueryEditor = async (app: Application, componentId: ParsedComponentId) => {
	const id = parseInt(componentId.ids[1] || "0");
	const query = id != 0 ? await rp.ms('extensions').get<Query>(`queries/${id}`, {
		fields: ['id_thread', 'description', 'id_custom_page', 'accounting']
	}) : {
		id_thread: 0,
		description: 'New query'
	};

	if (!query) return null;
	return createQuerySqlEditor(app, componentId, query);
}

const createSqlEditorResult = (app: Application, componentId: ParsedComponentId) => {
	return dom.createElement<SqlEditorResult>('rp-editor-sql-result');
}

const createSqlEditorQueryPlan = (app: Application, componentId: ParsedComponentId) => {
	return dom.createElement<SqlEditorResult>('rp-editor-sql-plan');
}

const createParamForm = (app: Application, componentId: ParsedComponentId) =>
	dom.createElement<FormContainer>('og-form-container', {
		adapter: new ParamFormAdapter(
			componentId.ids[1] ? parseInt(componentId.ids[1]) : null,
			componentId.ids[2] ? parseInt(componentId.ids[2]) : null
		),
		// listeners: [['form.save', () => tryRefreshSidebar('parameters')]]
	});

const createExtensionView = async (app: Application, componentId: ParsedComponentId): Promise<ExtensionHost | ExtensionSandboxHost> => {
	const idResource = componentId.ids[1];
	const resource = await rp.ms('extensions').get<Resource>(`resources/${idResource}`);
	return requiresSandbox(resource)
		? dom.createElement<ExtensionSandboxHost>('rp-extensionhost-sandbox', { resource, container: rp })
		: dom.createElement<ExtensionInternalHost>('rp-extensionhost-internal', { resource, container: rp });
}

const createResultGrid = (app: ExtensionApplication, componentId: ParsedComponentId) =>
	dom.createElement<SqlEditorResult>('rp-editor-sql-result', {});

const createSyncEditor = (app: ExtensionApplication, componentId: ParsedComponentId) => {
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new SyncGridEditorAdapter(new ExtSyncEditorAdapter());
	return component;
}

export const componentFactories = new Map<string, (app: ExtensionApplication, componentId: ParsedComponentId) => HTMLElement|Promise<HTMLElement>>([
	['extensions', createMainSidebar],
	['view', createExtensionView],
	['projectForm', createProjectForm],
	['projectCopyForm', createProjectCopyForm],
	['projectGrid', createProjectGrid],
	['resultGrid', createResultGrid],
	['file', createFileEditor],
	['fileForm', createFileForm],
	['fileGrid', createFileGrid],
	['sqlEditor', createSqlEditor],
	['htmlEditor', createHtmlEditor],
	['sheetEditor', createSheetEditor],
	['query', createQueryEditor],
	['queryForm', createQueryForm],
	['queryGrid', createQueryGrid],
	['queryResult', createSqlEditorResult],
	['queryPlan', createSqlEditorQueryPlan],
	['parameterForm', createParamForm],
	['parameterContent', createParameterContentSqlEditor],
	['parameterValue', createParameterValueSqlEditor],
	['parameterGrid', createParameterGrid],
	['externalResources', createExternalResourceGrid],
	['sqlDependencies', createSqlDependencyGrid],
	['sync', createSyncEditor],
]);
